import { Button, Modal } from '@/components/UI';
import type { SuggestedBundleRow } from './types';
import { formatPct01Number, formatSignedPct } from './helpers';
import { RAW_RETURN_SUM_LABEL, WINSORIZED_RETURN_SUM_LABEL } from './returnMetricCopy';

interface SuggestedBundlesModalProps {
    isOpen: boolean;
    selectedJobId: number | null;
    suggestions: SuggestedBundleRow[];
    isPending: boolean;
    onClose: () => void;
    onSelectBundle: (resultIds: number[]) => void;
    onCreateBundle: (resultIds: number[]) => void;
}

const SuggestedBundlesModal = ({
    isOpen,
    selectedJobId,
    suggestions,
    isPending,
    onClose,
    onSelectBundle,
    onCreateBundle
}: SuggestedBundlesModalProps) => {
    return (
        <Modal
            isOpen={isOpen}
            size="xl"
            title={selectedJobId !== null ? `Suggested bundles (Job #${selectedJobId})` : 'Suggested bundles'}
            onClose={onClose}
        >
            <div className="text-zinc-400 text-xs mb-3">
                Suggestions are greedy OR bundles built from top results. Use them when you find a valuable but limiting rule (e.g.,
                dividends) and want to broaden coverage without giving up too much lift. Return sums are arithmetic, not compounded or
                portfolio returns; holdout return sums are winsorized.
            </div>

            {isPending && suggestions.length === 0 ? (
                <div className="text-zinc-400 text-sm">Suggesting…</div>
            ) : suggestions.length === 0 ? (
                <div className="text-zinc-400 text-sm">No suggestions yet.</div>
            ) : (
                <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                    {suggestions.map((s, idx) => {
                        const ho = s.metrics.holdout;
                        return (
                            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3" key={`${s.resultIds.join(',')}-${idx}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white text-xs break-words mb-1">{s.ruleSummary}</div>
                                        <div className="text-zinc-500 text-[11px]">
                                            {s.resultIds.length} rules · ids=[{s.resultIds.join(',')}]
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button size="sm" variant="secondary" onClick={() => onSelectBundle(s.resultIds)}>
                                            Select
                                        </Button>
                                        <Button size="sm" variant="primary" onClick={() => onCreateBundle(s.resultIds)}>
                                            Create
                                        </Button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-zinc-400">
                                    <div>
                                        In-sample lift:{' '}
                                        <span className={s.metrics.inSample.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                            {formatSignedPct(s.metrics.inSample.lift, 2)}
                                        </span>
                                    </div>
                                    <div>
                                        In-sample avg:{' '}
                                        <span className={s.metrics.inSample.avgProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                            {formatSignedPct(s.metrics.inSample.avgProfit, 2)}
                                        </span>
                                        <div className="text-zinc-500 text-[10px]">
                                            expected: {formatSignedPct(s.metrics.inSample.baselineAvgProfit, 2)}
                                        </div>
                                    </div>
                                    <div>
                                        {RAW_RETURN_SUM_LABEL}:{' '}
                                        <span className={s.metrics.inSample.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                            {formatSignedPct(s.metrics.inSample.totalProfit, 2)}
                                        </span>
                                        <div className="text-zinc-500 text-[10px]">
                                            expected:{' '}
                                            {formatSignedPct(s.metrics.inSample.baselineAvgProfit * s.metrics.inSample.passingCount, 2)}
                                        </div>
                                    </div>
                                    <div>
                                        In-sample trades:{' '}
                                        <span className="text-white">{s.metrics.inSample.passingCount.toLocaleString()}</span>
                                        <div className="text-zinc-500 text-[10px]">
                                            {formatPct01Number(s.metrics.inSample.coverage, 1)} coverage
                                        </div>
                                    </div>
                                    <div>
                                        In-sample win:{' '}
                                        <span className="text-white">{formatPct01Number(s.metrics.inSample.winRate, 1)}</span>
                                    </div>
                                    {ho ? (
                                        <>
                                            <div>
                                                HO lift:{' '}
                                                <span className={ho.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                    {formatSignedPct(ho.lift, 2)}
                                                </span>
                                            </div>
                                            <div>
                                                HO win: <span className="text-white">{formatPct01Number(ho.winRate, 1)}</span>
                                            </div>
                                            <div>
                                                HO mean:{' '}
                                                <span className={ho.ruleMeanProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                    {formatSignedPct(ho.ruleMeanProfit, 2)}
                                                </span>
                                                <div className="text-zinc-500 text-[10px]">
                                                    expected: {formatSignedPct(ho.baselineMeanProfit, 2)}
                                                </div>
                                            </div>
                                            <div>
                                                {WINSORIZED_RETURN_SUM_LABEL}:{' '}
                                                <span
                                                    className={ho.ruleMeanProfit * ho.passingCount >= 0 ? 'text-green-400' : 'text-red-400'}
                                                >
                                                    {formatSignedPct(ho.ruleMeanProfit * ho.passingCount, 2)}
                                                </span>
                                                <div className="text-zinc-500 text-[10px]">
                                                    expected: {formatSignedPct(ho.baselineMeanProfit * ho.passingCount, 2)}
                                                </div>
                                            </div>
                                            <div>
                                                HO trades: <span className="text-white">{ho.passingCount.toLocaleString()}</span>
                                                <div className="text-zinc-500 text-[10px]">
                                                    {formatPct01Number(ho.coverage, 1)} coverage
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-span-2 md:col-span-4 text-zinc-500 text-xs">
                                            No holdout configured for this job.
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-3">
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};

export default SuggestedBundlesModal;
